import React, { Component } from "react";
import { observer } from "mobx-react";
import { Route } from "react-router-dom";
import { Provider } from "react-redux";
import { AxiosResponse, AxiosError } from "axios";
import { DefaultNavbar, MainNav, PaymentNavbar, Footer } from "./containers";
import currencyStore from "./stores/CurrencyStore";
import LoginStore from "./stores/LoginStore";
import LoginReactions from "./stores/LoginStore/LoginReactions";
import PricingStore from "./stores/PricingStore";
import PricingReactions from "./stores/PricingStore/PricingReactions";
import ProfileStore from "./stores/ProfileStore";
import ProfileReactions from "./stores/ProfileStore/ProfileReactions";
import RecentNotifications from "./containers/RecentNotifications";
import NotificationIcons from "./components/Notifications/Icons";
import EmbeddedContent from "./containers/EmbeddedContent";
import PromoBar from "./components/PromoBar";
import NewsletterPopup from "./containers/NewsletterPopup";
import HalloweenPopup from "./containers/HalloweenPopup";
import {
  CurrentUserResource,
  XpResource,
} from "./stores/CurrentUserStore/Types";
import { ProfileSidebarView } from "./components/Profile/ProfileSidebar";
import { getRequest, postRequest } from "./utils/httpRequest";
import { store } from "./stores/CommunityStore";

interface State {
  showSignInSidebar: boolean;
  showLoggedInSidebar: boolean;
  showNotificationModal: boolean;
  redirectUrl: string;
  currentUser: CurrentUserResource;
  currentUserLoaded: boolean;
  selectedCurrency: string;
  userXp: number;
  showProfileSidebar: boolean;
  profileSidebarView: ProfileSidebarView;
}

interface Props {
  computedMatch?: {
    params: any;
    path: any;
  };
  // Handed down by the router's Switch along with computedMatch.
  location?: {
    pathname: string;
    search: string;
  };
  exact: boolean;
  path: any;
  component: any;
  layout?: string;
  mode?: string;
  showRecentNotifications?: boolean;
}

class DynamicLayoutRoute extends Component<Props, State> {
  loginStore = new LoginStore();
  pricingStore = new PricingStore();
  profileStore = new ProfileStore();
  // The router's history, picked up from the Route below, so this component
  // can change the address in a way the router knows about.
  routerHistory: {
    replace: (location: {
      pathname: string;
      search: string;
      hash: string;
      state?: unknown;
    }) => void;
    location?: { state?: unknown };
  } | null = null;

  constructor(props) {
    super(props);

    this.state = {
      showSignInSidebar: false,
      showLoggedInSidebar: false,
      showNotificationModal: false,
      showProfileSidebar: false,
      redirectUrl: "",
      currentUser: {
        id: null,
        kind: "current-user-absent",
        selected_currency: currencyStore.isoCode,
        admin: false,
        can_access_community: false,
        community_rules_accepted: false,
      },
      currentUserLoaded: false,
      userXp: 0,
      selectedCurrency: "",
      profileSidebarView: "account",
    };
  }

  componentDidMount() {
    this.loginStore.loading();
    this.pricingStore.loading();
    this.profileStore.loading();

    this.getCurrentUser();
  }

  componentDidUpdate(
    prevProps: Readonly<any>,
    prevState: Readonly<State>,
    snapshot?: any,
  ) {
    const isEnrolmentPath = this.props.computedMatch.path.includes(
      "enrolments/:enrolmentId/questions",
    );

    if (!isEnrolmentPath) {
      $("chat-app").css({ display: "none" });
    }

    // Moving between pages inside the app reuses this component, so the
    // settings link only ran on a full page load before. Watch the address
    // change as well.
    const location = this.props.location;
    const previous = prevProps.location;
    if (
      location &&
      (location.pathname !== previous?.pathname ||
        location.search !== previous?.search) &&
      this.state.currentUser?.kind === "current-user"
    ) {
      this.openSettingsFromLink();
    }
  }

  getCurrentUser = () => {
    const enrolmentId = this.props.computedMatch.params.enrolmentId;

    return getRequest(`/api/current_user.json?enrolment_id=${enrolmentId}`)
      .then((response: AxiosResponse<CurrentUserResource>) => {
        this.setState({
          currentUser: response.data,
          currentUserLoaded: true,
          selectedCurrency: response.data.selected_currency,
        });

        if (response.data.kind === "current-user") {
          this.getXp(response.data.id);
          this.openSettingsFromLink();

          (window as any).honeybadger?.setContext({
            user_id: response.data.id,
            user_email: response.data.email,
          });
        }
      })
      .catch((error) => {
        this.setState({ currentUserLoaded: true });
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  // The failed-payment email links to /profile?settings=plan so the person
  // lands on the card form rather than hunting for it.
  openSettingsFromLink = () => {
    const wanted = new URLSearchParams(window.location.search).get("settings");
    if (!wanted || !window.location.pathname.startsWith("/profile")) return;

    this.setState({ showProfileSidebar: true, profileSidebarView: "settings" });

    // One use only: take the instruction out of the address, so closing the
    // sidebar stays closed when the page refreshes its data or is reloaded.
    // It goes through the router's own history, so the router's idea of the
    // address changes too; the browser's history is only the fallback.
    const params = new URLSearchParams(window.location.search);
    params.delete("settings");
    const rest = params.toString();
    const search = rest ? `?${rest}` : "";

    if (this.routerHistory) {
      this.routerHistory.replace({
        pathname: window.location.pathname,
        search,
        hash: window.location.hash,
        state: this.routerHistory.location?.state,
      });
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${search}${window.location.hash}`,
      );
    }
  };

  getXp = (userId) => {
    return getRequest(`/api/users/${userId}/xps.json`)
      .then((response: AxiosResponse<XpResource>) => {
        this.setState({
          userXp: response.data.xp,
        });
      })
      .catch((error) => {
        (window as any).honeybadger?.notify(error?.response?.data?.error);
      });
  };

  actualRouteComponent = () => {
    const { component: RoutedComponent, ...rest } = this.props;
    return (
      <div id="landing">
        <Route
          {...rest}
          render={(props) => {
            this.routerHistory = props.history;
            return (
              <RoutedComponent
                {...props}
                {...rest}
                handleShowSignInSideBar={this.handleShowSignInSideBar}
                pricingStore={this.pricingStore}
                profileStore={this.profileStore}
                getCurrentUser={this.getCurrentUser}
                currentUser={this.state.currentUser}
                currentUserLoaded={this.state.currentUserLoaded}
                showNotificationModal={this.state.showNotificationModal}
                handleShowNotificationModal={this.handleShowNotificationModal}
                selectedCurrency={this.state.selectedCurrency}
                getXp={this.getXp}
                showProfileSidebar={this.state.showProfileSidebar}
                profileSidebarView={this.state.profileSidebarView}
                handleShowProfileSidebar={this.handleShowProfileSidebar}
                handleShowLoggedInSideBar={this.handleShowLoggedInSideBar}
              />
            );
          }}
        />
      </div>
    );
  };

  handleShowSignInSideBar = (redirectUrl?: string) => {
    const afterLoginUrl = redirectUrl || "";
    this.setState({ showSignInSidebar: true, redirectUrl: afterLoginUrl });

    if (redirectUrl) {
      postRequest("/api/set_enroller_path", {
        enroller_path: afterLoginUrl,
      })
        .then(() => console.log("set"))
        .catch((error) => {
          (window as any).honeybadger?.notify(error?.response?.data?.error);
        });
    }
    const that = this;

    // $(".pusher").bind("click", function () {
    //   that.handleHideSignInSideBar();
    // });
  };

  handleHideSignInSideBar = () => {
    this.setState({ showSignInSidebar: false, redirectUrl: "" });
  };

  handleShowLoggedInSideBar = () => {
    this.setState({ showLoggedInSidebar: true });
    // $(".pusher").addClass("dimmed");
    //
    // const that = this;
    //
    // $(".pusher").bind("click", function () {
    //   that.handleHideLoggedInSideBar();
    // });
  };

  handleHideLoggedInSideBar = () => {
    this.setState({ showLoggedInSidebar: false });
  };

  handleShowNotificationModal = (val) =>
    this.setState({ showNotificationModal: val });

  onClickHeaderLogo = () => {
    (window as any).location = "/";
  };

  loginUser = (e: React.MouseEvent<HTMLElement>) => {
    const { redirectUrl } = this.state;

    e.preventDefault();
    this.loginStore.saving(redirectUrl);
  };

  updateSelectedCurrency = (isoCode: string) => {
    this.setState({ selectedCurrency: isoCode });
  };

  handleShowProfileSidebar = (value: boolean, view?: ProfileSidebarView) => {
    if (view) {
      this.setState({ showProfileSidebar: value, profileSidebarView: view });
    } else {
      this.setState({ showProfileSidebar: value });
    }
  };

  renderNav = () => {
    const { layout, showRecentNotifications } = this.props;
    const {
      showSignInSidebar,
      showLoggedInSidebar,
      redirectUrl,
      currentUser,
      userXp,
      showProfileSidebar,
      profileSidebarView,
    } = this.state;
    const promoContext = this.promoContext();
    const renderGlobalPromo = promoContext !== "course";

    const hideGlobalFooter =
      layout === "lesson" &&
      /^\/enrolments\/[^/]+\/questions(\/|$)/.test(window.location.pathname);

    switch (layout) {
      case "setup":
        return <div>{this.actualRouteComponent()}</div>;
      case "payment":
        return (
          <div>
            <PaymentNavbar currentUser={currentUser} />
            <PromoBar currentUser={currentUser} pageContext="checkout" />
            {this.actualRouteComponent()}
            <Footer
              currentUser={currentUser}
              pricingStore={this.pricingStore}
              updateSelectedCurrency={this.updateSelectedCurrency}
              layout={layout}
            />
          </div>
        );
      default: {
        return (
          <div>
            {/* One hidden sprite for the whole layout: both the bell dropdown
                in the nav and the alerts page reference these symbols, and
                defining them twice would duplicate DOM ids. */}
            <NotificationIcons />
            <MainNav
              showSignInSidebar={showSignInSidebar}
              handleShowSignInSideBar={this.handleShowSignInSideBar}
              handleHideSignInSideBar={this.handleHideSignInSideBar}
              showLoggedInSidebar={showLoggedInSidebar}
              handleShowLoggedInSideBar={this.handleShowLoggedInSideBar}
              handleHideLoggedInSideBar={this.handleHideLoggedInSideBar}
              loginStore={this.loginStore}
              loginUser={this.loginUser}
              redirectUrl={redirectUrl}
              onClickHeaderLogo={this.onClickHeaderLogo}
              currentUser={currentUser}
              profileStore={this.profileStore}
              getCurrentUser={this.getCurrentUser}
              handleShowNotificationModal={this.handleShowNotificationModal}
              showNotificationModal={this.state.showNotificationModal}
              layout={layout}
              userXp={userXp}
              showProfileSidebar={showProfileSidebar}
              profileSidebarView={profileSidebarView}
              handleShowProfileSidebar={this.handleShowProfileSidebar}
            />
            {renderGlobalPromo && (
              <PromoBar currentUser={currentUser} pageContext={promoContext} />
            )}
            {this.actualRouteComponent()}
            {!hideGlobalFooter && (
              <Footer
                currentUser={currentUser}
                pricingStore={this.pricingStore}
                updateSelectedCurrency={this.updateSelectedCurrency}
                layout={layout}
              />
            )}
            <RecentNotifications
              showRecentNotifications={showRecentNotifications}
            />
            <EmbeddedContent />
            {/* Everywhere except checkout and setup, which never reach this
                branch; lessons, where someone is mid-answer and paying us for
                the privilege of not being interrupted; and the pages that
                confirm a purchase, where someone has just paid and a plea for
                their email address is the wrong first thing to see. */}
            {this.newsletterPopupAllowed() && (
              <NewsletterPopup
                pageContext={promoContext}
                pathname={window.location.pathname}
              />
            )}
            {/* The same pages as the newsletter popup, which stands down
                while this runs, less /halloween itself, where the game is
                the page. */}
            {this.newsletterPopupAllowed() &&
              window.location.pathname !== "/halloween" && (
                <HalloweenPopup pageContext={promoContext} />
              )}
          </div>
        );
      }
    }
  };

  // The newsletter popup stays off every lesson layout (there are two) and off
  // the three purchase confirmations, which carry no layout of their own.
  newsletterPopupAllowed = (): boolean => {
    const { layout } = this.props;
    if (layout && layout.startsWith("lesson")) return false;

    const pathname = window.location.pathname;
    const isPurchaseConfirmation =
      /^\/payments\/successful\//.test(pathname) ||
      /^\/passes\/[^/]+\/success\/?$/.test(pathname) ||
      /^\/gift\/[^/]+\/success\/?$/.test(pathname);

    return !isPurchaseConfirmation;
  };

  promoContext = (): "homepage" | "course" | "logged-in" | "checkout" => {
    const { layout } = this.props;
    const pathname = window.location.pathname;
    const isCoursePage = /^\/courses\/[^/]+(\/upcoming)?\/?$/.test(pathname);
    const isProfilePage = /^\/profile\/?$/.test(pathname);

    if (layout === "payment") {
      return "checkout";
    }

    if (isCoursePage) {
      return "course";
    }

    if (this.state.currentUser.kind === "current-user" && isProfilePage) {
      return "logged-in";
    }

    return "homepage";
  };

  render() {
    const { layout } = this.props;
    return (
      <>
        <Provider store={store}>
          {this.renderNav()}
          <LoginReactions store={this.loginStore} />
          {layout !== "payment" && (
            <PricingReactions
              store={this.pricingStore}
              params={this.props.computedMatch.params}
            />
          )}
          {layout !== "payment" && (
            <ProfileReactions store={this.profileStore} />
          )}
        </Provider>
      </>
    );
  }
}

export default observer(DynamicLayoutRoute);
